<template>
	<div class="hud">
		<button
			v-show="!open"
			ref="fab"
			type="button"
			class="hud-fab"
			:class="{ 'hud-fab--pulse': fabPulse }"
			aria-label="Open behaviour insights"
			@click="openPanel"
		>
			<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
				<path d="M4 19V10M10 19V5M16 19v-6M22 19H2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
			</svg>
			<span v-if="trackerState.buffered" class="hud-fab__count">{{ trackerState.buffered }}</span>
		</button>

		<section
			v-if="open"
			ref="panel"
			class="hud-panel"
			:class="{ 'hud-panel--wide': expanded, 'hud-panel--dragging': drag }"
			:style="panelStyle"
			role="dialog"
			aria-label="Behaviour insights"
		>
			<header class="hud-head" @pointerdown="startDrag">
				<div class="hud-head__title">
					<h2>Behaviour insights</h2>
					<p>{{ headline }}</p>
				</div>
				<div class="hud-head__actions">
					<button type="button" class="hud-icon" aria-label="How this panel works" title="How this panel works" @click="openHelp">
						<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17.2v.1" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
					</button>
					<button
						type="button"
						class="hud-icon"
						:disabled="resetting"
						aria-label="Reset profile"
						title="Reset profile"
						@click="askReset"
					>
						<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
					</button>
					<button type="button" class="hud-icon" :aria-label="expanded ? 'Shrink panel' : 'Expand panel'" @click="expanded = !expanded">
						<svg v-if="!expanded" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
						<svg v-else viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M20 10h-6V4M4 14h6v6M14 10l7-7M10 14l-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
					</button>
					<button type="button" class="hud-icon" aria-label="Close insights" @click="closePanel">
						<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
					</button>
				</div>
			</header>

			<nav class="hud-tabs" role="tablist">
				<button
					v-for="tab in tabs"
					:key="tab.key"
					type="button"
					role="tab"
					class="hud-tab"
					:class="{ 'hud-tab--active': activeTab === tab.key }"
					:aria-selected="activeTab === tab.key"
					@click="selectTab(tab.key)"
				>
					{{ tab.label }}
					<span v-if="tab.key === 'events' && trackerState.buffered" class="hud-tab__badge">{{ trackerState.buffered }}</span>
				</button>
			</nav>

			<div class="hud-body">
				<div v-if="blockingMessage" class="hud-empty">
					<p>{{ blockingMessage }}</p>
					<button v-if="canRetry" type="button" class="hud-button" @click="retry">Try again</button>
				</div>
				<template v-else>
					<ProfileTab
						v-if="activeTab === 'profile'"
						:insights="insights"
						:baseline="baseline"
						:loading="loading"
					/>
					<DecayTab
						v-else-if="activeTab === 'decay'"
						:signals="insights && insights.signals"
						:received-at="insightsAt"
						:loading="loading"
					/>
					<HistoryTab
						v-else-if="activeTab === 'history'"
						:history="history"
						:top-card-clicks="insights && insights.profile ? insights.profile.topCardClicks : null"
						:loading="historyLoading"
						:error="historyError"
					/>
					<EventsTab
						v-else-if="activeTab === 'events'"
						:log="log"
						:tracker-state="trackerState"
						:last-flush="lastFlush"
						:expanded="expanded"
						@clear="clearLog"
						@expand="expanded = true"
					/>
					<RecommendationsTab
						v-else-if="activeTab === 'recommendations'"
						:recommendations="recommendations"
						:domains="domains"
						:loading="recsLoading"
						:error="recsError"
						@refresh="resumeAfterFailure(); loadInsights({ recommendations: true })"
					/>
				</template>
			</div>

			<HelpGuide
				v-if="showHelp"
				:half-lives="insights && insights.signals ? insights.signals.halfLifeSeconds : null"
				:test-mode="Boolean(insights && insights.signals && insights.signals.testMode)"
				@close="closeHelp"
			/>

			<div v-if="confirmingReset" class="hud-confirm" @pointerdown.self="cancelReset">
				<div class="hud-confirm__box" role="alertdialog" aria-modal="true" aria-labelledby="hud-confirm-title">
					<h3 id="hud-confirm-title">Reset profile?</h3>
					<p>This deletes all your activity and scores. Your profile starts again from zero.</p>
					<p v-if="resetError" class="hud-confirm__error" role="alert">{{ resetError }}</p>
					<div class="hud-confirm__actions">
						<button ref="confirmYes" type="button" class="hud-confirm__yes" :disabled="resetting" @click="resetProfile">
							{{ resetting ? "Resetting…" : "Yes, reset" }}
						</button>
						<button type="button" class="hud-confirm__cancel" :disabled="resetting" @click="cancelReset">Cancel</button>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import { getHudInsights, resetHudProfile } from "@/utils/apiFunctions";
import { hasAccess } from "@/utils/tokenCheck";
import { tracker } from "@/tracking/tracker";
import { subscribe } from "@/tracking/inspector";
import ProfileTab from "./ProfileTab.vue";
import DecayTab from "./DecayTab.vue";
import EventsTab from "./EventsTab.vue";
import RecommendationsTab from "./RecommendationsTab.vue";
import HistoryTab from "./HistoryTab.vue";
import HelpGuide from "./HelpGuide.vue";

const POLL_MS = 15000;
// Kept small: the log is for watching a session, not an archive.
const MAX_LOG = 500;
const EDGE = 8;
// A click outside closes the panel only if no navigation follows it.
const OUTSIDE_CLOSE_DELAY_MS = 250;
// A slow navigation (async route guard, full page load) still reopens it within this window.
const NAVIGATION_GRACE_MS = 2000;
// The shared client refreshes the login token on every network failure, so polling stops after this many in a row.
const MAX_FAILED_POLLS = 2;
// Per-viewer convenience only: the panel reopens where it was after a page refresh.
const STORAGE_KEY = "hire3x_insights_hud";
const TAB_KEYS = ["profile", "decay", "history", "events", "recommendations"];
// Set once the guide has been seen, so it opens by itself only the first time.
const HELP_SEEN_KEY = "hire3x_insights_help_seen";

export default {
	name: "InsightsHud",
	components: { ProfileTab, DecayTab, HistoryTab, EventsTab, RecommendationsTab, HelpGuide },
	data() {
		return {
			open: false,
			expanded: false,
			activeTab: "profile",
			tabs: [
				{ key: "profile", label: "Profile" },
				{ key: "decay", label: "Decay" },
				{ key: "history", label: "History" },
				{ key: "events", label: "Live events" },
				{ key: "recommendations", label: "Recommendations" },
			],
			insights: null,
			insightsAt: null,
			// Bumped by a reset, so a read that started before it is dropped instead of shown.
			generation: 0,
			resetting: false,
			resetError: null,
			confirmingReset: false,
			showHelp: false,
			baseline: null,
			loading: false,
			error: null,
			recommendations: null,
			recsLoading: false,
			recsError: null,
			history: null,
			historyLoading: false,
			historyError: null,
			log: [],
			trackerState: tracker.getState(),
			lastFlush: null,
			fabPulse: false,
			position: null,
			drag: null,
			pollTimer: null,
			failedPolls: 0,
		};
	},
	computed: {
		panelStyle() {
			if (!this.position) return {};
			return { left: `${this.position.x}px`, top: `${this.position.y}px`, right: "auto", bottom: "auto" };
		},
		headline() {
			const profile = this.insights && this.insights.profile;
			if (this.loading && !profile) return "Loading your profile…";
			if (!profile) return "Your live behaviour profile";
			const events = profile.totalEvents || 0;
			return `${events} event${events === 1 ? "" : "s"} processed · refreshes every 15 s`;
		},
		domains() {
			const career = this.insights && this.insights.profile && this.insights.profile.careerDirection;
			return (career && career.domains) || [];
		},
		blockingMessage() {
			if (["events", "recommendations", "history"].includes(this.activeTab)) return null;
			return this.insights ? null : this.error;
		},
		canRetry() {
			return this.error && this.error !== SIGNED_OUT && this.error !== UNAVAILABLE_HERE;
		},
	},
	created() {
		// Subscribed from app start, so the log already holds events from before the panel opened.
		this.unsubscribe = subscribe(this.onTrackerEvent);
		// Not reactive: timing bookkeeping for outside clicks, never rendered.
		this.outsideCloseTimer = null;
		this.closedByOutsideAt = 0;
		this.restoreState();
	},
	mounted() {
		document.addEventListener("pointerdown", this.onOutsidePointer, true);
		this.removeRouteHook = this.$router.beforeEach((to, from, next) => {
			this.onNavigation(false);
			next();
		});
		window.addEventListener("pagehide", this.onPageHide);
		// Reopened after a refresh if it was open before.
		if (this.open) this.openPanel();
		document.addEventListener("visibilitychange", this.onVisibility);
		window.addEventListener("resize", this.keepInView);
	},
	beforeDestroy() {
		if (this.unsubscribe) this.unsubscribe();
		this.stopPolling();
		this.endDrag();
		document.removeEventListener("pointerdown", this.onOutsidePointer, true);
		document.removeEventListener("visibilitychange", this.onVisibility);
		window.removeEventListener("resize", this.keepInView);
		window.removeEventListener("pagehide", this.onPageHide);
		if (this.removeRouteHook) this.removeRouteHook();
		this.cancelOutsideClose();
	},
	methods: {
		openPanel() {
			this.open = true;
			if (!helpSeen()) {
				this.showHelp = true;
			}
			this.failedPolls = 0;
			this.trackerState = tracker.getState();
			this.$nextTick(this.keepInView);
			this.loadInsights({ recommendations: this.activeTab === "recommendations", history: this.activeTab === "history" });
			this.startPolling();
		},
		closePanel() {
			this.open = false;
			this.stopPolling();
			this.endDrag();
		},
		selectTab(key) {
			this.activeTab = key;
			this.resumeAfterFailure();
			if (key === "recommendations" && !this.recommendations && !this.recsLoading) this.loadInsights({ recommendations: true });
			if (key === "history" && !this.historyLoading) this.loadInsights({ history: true });
		},
		startPolling() {
			this.stopPolling();
			this.pollTimer = setInterval(() => {
				// Recommendations cost vector searches, so they load on request; history refreshes while shown.
				if (document.visibilityState !== "visible") return;
				if (this.failedPolls >= MAX_FAILED_POLLS) {
					this.stopPolling();
					return;
				}
				this.loadInsights({ history: this.activeTab === "history" });
			}, POLL_MS);
		},
		resumeAfterFailure() {
			if (this.failedPolls < MAX_FAILED_POLLS) return;
			this.failedPolls = 0;
			if (this.open) this.startPolling();
		},
		retry() {
			this.resumeAfterFailure();
			this.loadInsights();
		},
		stopPolling() {
			if (this.pollTimer) clearInterval(this.pollTimer);
			this.pollTimer = null;
		},
		onVisibility() {
			if (this.open && document.visibilityState === "visible" && this.failedPolls < MAX_FAILED_POLLS) {
				this.loadInsights({ history: this.activeTab === "history" });
			}
		},
		async loadInsights({ recommendations = false, history = false } = {}) {
			if (!hasAccess()) {
				this.error = SIGNED_OUT;
				return;
			}
			const flag = recommendations ? "recsLoading" : history ? "historyLoading" : "loading";
			if (this[flag]) return;
			this[flag] = true;
			if (recommendations) this.recsError = null;
			if (history) this.historyError = null;
			const generation = this.generation;
			try {
				const res = await getHudInsights({ recommendations, history });
				if (generation !== this.generation) return;
				// The shared client resolves 4xx/5xx instead of rejecting, so check the status here.
				if (!res || res.status !== 200) throw { response: res };
				const payload = res.data && (res.data.data || res.data);
				if (!payload || !payload.profile) throw new Error("empty");
				this.applyInsights(payload);
				if (recommendations) this.recommendations = payload.recommendations || null;
				if (history) this.history = payload.history || null;
				this.error = null;
				this.failedPolls = 0;
			} catch (err) {
				if (generation !== this.generation) return;
				this.failedPolls += 1;
				const message = errorMessage(err);
				if (recommendations) this.recsError = message;
				else if (history) this.historyError = message;
				else this.error = message;
			} finally {
				// A reset already cleared the flag, and the read after it may hold it now.
				if (generation === this.generation) this[flag] = false;
			}
		},
		applyInsights(payload) {
			// The first read of the session is the baseline every later score is compared with.
			if (!this.baseline) this.baseline = scoreMap(payload.signals);
			this.insights = payload;
			this.insightsAt = Date.now();
		},
		openHelp() {
			this.showHelp = true;
		},
		closeHelp() {
			this.showHelp = false;
			try {
				localStorage.setItem(HELP_SEEN_KEY, "1");
			} catch (err) {
				// Storage blocked: the guide opens again next time, which is harmless.
			}
		},
		askReset() {
			this.resetError = null;
			this.confirmingReset = true;
			this.$nextTick(() => this.$refs.confirmYes && this.$refs.confirmYes.focus());
		},
		cancelReset() {
			if (!this.resetting) this.confirmingReset = false;
		},
		async resetProfile() {
			if (this.resetting) return;
			this.resetting = true;
			this.resetError = null;
			try {
				const res = await resetHudProfile();
				if (!res || res.status !== 200) throw { response: res };
				this.generation += 1;
				// Every in-flight flag is cleared too, so the fresh read below is never skipped as a duplicate.
				Object.assign(this, {
					insights: null, insightsAt: null, baseline: null, recommendations: null, history: null,
					loading: false, recsLoading: false, historyLoading: false, error: null, failedPolls: 0,
				});
				this.confirmingReset = false;
				this.resumeAfterFailure();
				await this.loadInsights();
			} catch (err) {
				const status = err && err.response && err.response.status;
				// A part-finished reset is safe to repeat: what it deleted stays deleted.
				this.resetError = status === 404 ? UNAVAILABLE_HERE
					: status === 401 ? SIGNED_OUT
						: "Reset did not finish. Try again.";
			} finally {
				this.resetting = false;
			}
		},
		onTrackerEvent(type, detail) {
			this.trackerState = tracker.getState();
			if (type === "tracked") {
				this.pushLog({ kind: "event", id: detail.event.eventId, at: Date.parse(detail.event.occurredAt) || Date.now(), name: detail.event.name, props: detail.event.props });
				this.pulseFab();
			} else if (type === "flush") {
				// Logged as sending; the bar only flashes once the SDK reports the send's result.
				this.pushLog({ kind: "batch", id: detail.batchId, at: detail.at, reason: detail.reason, count: detail.count, outcome: detail.reason === "pagehide" ? "beacon" : "sending" });
			} else if (type === "sent") {
				const row = this.log.find((entry) => entry.id === detail.batchId);
				if (row) row.outcome = detail.outcome;
				this.lastFlush = { batchId: detail.batchId, reason: row ? row.reason : "interval", count: detail.count, outcome: detail.outcome, at: detail.at };
			}
		},
		pushLog(entry) {
			this.log.unshift(entry);
			if (this.log.length > MAX_LOG) this.log.splice(MAX_LOG);
		},
		clearLog() {
			this.log = [];
		},
		pulseFab() {
			if (this.open) return;
			this.fabPulse = false;
			this.$nextTick(() => {
				this.fabPulse = true;
			});
		},
		onOutsidePointer(event) {
			if (!this.open) {
				// A later click is a new decision; only the closing click's own navigation undoes the close.
				this.closedByOutsideAt = 0;
				return;
			}
			if (this.outsideCloseTimer) return;
			const panel = this.$refs.panel;
			if (panel && panel.contains(event.target)) return;
			this.outsideCloseTimer = setTimeout(() => {
				this.outsideCloseTimer = null;
				this.closePanel();
				this.closedByOutsideAt = Date.now();
			}, OUTSIDE_CLOSE_DELAY_MS);
		},
		cancelOutsideClose() {
			if (this.outsideCloseTimer) clearTimeout(this.outsideCloseTimer);
			this.outsideCloseTimer = null;
		},
		// A navigation keeps the panel as it was: cancel a pending close, or undo a close it just caused.
		onNavigation(unloading) {
			if (this.outsideCloseTimer) {
				this.cancelOutsideClose();
				return;
			}
			if (this.open || !this.closedByOutsideAt) return;
			const recent = Date.now() - this.closedByOutsideAt < NAVIGATION_GRACE_MS;
			this.closedByOutsideAt = 0;
			if (!recent) return;
			if (unloading) {
				// No render ahead, only the saved state for the next page.
				this.open = true;
				this.saveState();
			} else {
				this.openPanel();
			}
		},
		onPageHide() {
			this.onNavigation(true);
		},
		restoreState() {
			try {
				const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
				if (!saved || typeof saved !== "object") return;
				this.open = saved.open === true;
				this.expanded = saved.expanded === true;
				if (TAB_KEYS.includes(saved.activeTab)) this.activeTab = saved.activeTab;
				const pos = saved.position;
				if (pos && isFinite(pos.x) && isFinite(pos.y)) this.position = { x: Number(pos.x), y: Number(pos.y) };
			} catch (err) {
				// Storage blocked or unreadable: start closed, as before.
			}
		},
		saveState() {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify({
					open: this.open, expanded: this.expanded, activeTab: this.activeTab, position: this.position,
				}));
			} catch (err) {
				// Storage blocked: the panel still works, it just won't reopen after a refresh.
			}
		},
		startDrag(event) {
			// Buttons in the header keep their click; a small screen keeps the docked sheet.
			if (event.button !== 0 || event.target.closest("button") || window.innerWidth < 640) return;
			const rect = this.$refs.panel.getBoundingClientRect();
			this.drag = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
			window.addEventListener("pointermove", this.onDrag);
			window.addEventListener("pointerup", this.endDrag);
			event.preventDefault();
		},
		onDrag(event) {
			if (!this.drag) return;
			this.position = this.clamp(event.clientX - this.drag.dx, event.clientY - this.drag.dy);
		},
		endDrag() {
			if (this.drag) this.saveState();
			this.drag = null;
			window.removeEventListener("pointermove", this.onDrag);
			window.removeEventListener("pointerup", this.endDrag);
		},
		clamp(x, y) {
			const panel = this.$refs.panel;
			const width = panel ? panel.offsetWidth : 0;
			const height = panel ? panel.offsetHeight : 0;
			return {
				x: Math.min(Math.max(EDGE, x), Math.max(EDGE, window.innerWidth - width - EDGE)),
				y: Math.min(Math.max(EDGE, y), Math.max(EDGE, window.innerHeight - height - EDGE)),
			};
		},
		keepInView() {
			if (this.position) this.position = this.clamp(this.position.x, this.position.y);
		},
	},
	watch: {
		expanded() {
			this.$nextTick(this.keepInView);
			this.saveState();
		},
		open() {
			this.saveState();
		},
		activeTab() {
			this.saveState();
		},
	},
};

const SIGNED_OUT = "Sign in to see your behaviour profile.";
const UNAVAILABLE_HERE = "Insights are switched off in this environment.";

function errorMessage(err) {
	const status = err && err.response && err.response.status;
	if (status === 404) return UNAVAILABLE_HERE;
	if (status === 429) return "Insights are busy. Try again in a few seconds.";
	if (status === 401) return SIGNED_OUT;
	return "Insights could not load. Check the pipeline is running, then try again.";
}

// Every career node, including those still below the evidence bar, so their first rise shows.
function scoreMap(signals) {
	const map = {};
	((signals && signals.career) || []).forEach((domain) => {
		map[domain.tag] = domain.score;
		(domain.subdomains || []).forEach((sub) => {
			map[`${domain.tag}/${sub.tag}`] = sub.score;
		});
	});
	return map;
}

function helpSeen() {
	try {
		return localStorage.getItem(HELP_SEEN_KEY) === "1";
	} catch (err) {
		return true;
	}
}
</script>

<style scoped>
.hud {
	--hud-brand: #20639b;
	--hud-brand-deep: #1a4164;
	--hud-accent: #4a7eff;
	--hud-soft: #c4dcf6;
	--hud-surface: #f6f9fb;
	--hud-line: #e2e8f0;
	--hud-text: #333333;
	--hud-muted: #72738e;
	--hud-good: #038e00;
	--hud-warn: #ffc145;
	font-family: "Lato", sans-serif;
	color: var(--hud-text);
}

.hud-fab {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 1060;
	width: 52px;
	height: 52px;
	border: none;
	border-radius: 50%;
	background: var(--hud-brand);
	color: #fff;
	display: grid;
	place-items: center;
	box-shadow: 0 6px 18px rgba(26, 65, 100, 0.35);
	cursor: pointer;
	transition: transform 0.15s ease, background 0.15s ease;
}
.hud-fab:hover {
	background: var(--hud-brand-deep);
	transform: translateY(-1px);
}
.hud-fab:focus-visible,
.hud-icon:focus-visible,
.hud-tab:focus-visible,
.hud-button:focus-visible {
	outline: 2px solid var(--hud-accent);
	outline-offset: 2px;
}
.hud-fab__count {
	position: absolute;
	top: -4px;
	right: -4px;
	min-width: 20px;
	height: 20px;
	padding: 0 5px;
	border-radius: 10px;
	background: var(--hud-warn);
	color: var(--hud-brand-deep);
	font: 700 11px/20px "Montserrat", sans-serif;
}
.hud-fab--pulse {
	animation: hud-pulse 0.6s ease-out;
}
@keyframes hud-pulse {
	0% { box-shadow: 0 0 0 0 rgba(74, 126, 255, 0.55); }
	100% { box-shadow: 0 0 0 14px rgba(74, 126, 255, 0); }
}

.hud-panel {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 1060;
	width: 400px;
	height: min(600px, calc(100vh - 48px));
	display: flex;
	flex-direction: column;
	background: #fff;
	border: 1px solid var(--hud-line);
	border-radius: 14px;
	box-shadow: 0 18px 48px rgba(26, 65, 100, 0.22);
	overflow: hidden;
	animation: hud-open 0.18s ease-out;
}
.hud-panel--wide {
	width: min(880px, calc(100vw - 48px));
	height: calc(100vh - 48px);
}
.hud-panel--dragging {
	user-select: none;
	box-shadow: 0 24px 60px rgba(26, 65, 100, 0.3);
}
@keyframes hud-open {
	from { opacity: 0; transform: translateY(8px) scale(0.98); }
	to { opacity: 1; transform: none; }
}

.hud-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	padding: 14px 14px 10px 18px;
	background: var(--hud-brand);
	color: #fff;
	cursor: grab;
	touch-action: none;
}
.hud-panel--dragging .hud-head {
	cursor: grabbing;
}
.hud-head h2 {
	margin: 0;
	font: 700 15px/1.3 "Montserrat", sans-serif;
	color: #fff;
}
.hud-head p {
	margin: 2px 0 0;
	font-size: 12px;
	color: var(--hud-soft);
}
.hud-head__actions {
	display: flex;
	gap: 4px;
}
.hud-icon {
	width: 30px;
	height: 30px;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: #fff;
	display: grid;
	place-items: center;
	cursor: pointer;
}
.hud-icon:hover {
	background: rgba(255, 255, 255, 0.16);
}
.hud-icon:disabled {
	cursor: default;
}
.hud-confirm {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: grid;
	place-items: center;
	padding: 20px;
	border-radius: 14px;
	background: rgba(26, 65, 100, 0.35);
}
.hud-confirm__box {
	width: 100%;
	max-width: 320px;
	padding: 18px;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 12px 32px rgba(26, 65, 100, 0.25);
}
.hud-confirm__box h3 {
	margin: 0 0 6px;
	font: 700 15px "Montserrat", sans-serif;
	color: var(--hud-brand-deep);
}
.hud-confirm__box p {
	margin: 0 0 12px;
	font-size: 13px;
	line-height: 1.5;
	color: var(--hud-text);
}
.hud-confirm__error {
	color: #b3261e !important;
}
.hud-confirm__actions {
	display: flex;
	gap: 10px;
}
.hud-confirm__yes,
.hud-confirm__cancel {
	padding: 7px 14px;
	border-radius: 16px;
	font: 600 12.5px "Montserrat", sans-serif;
	cursor: pointer;
}
.hud-confirm__yes {
	border: none;
	background: #b3261e;
	color: #fff;
}
.hud-confirm__cancel {
	border: 1px solid var(--hud-line);
	background: #fff;
	color: var(--hud-text);
}
.hud-confirm__yes:disabled,
.hud-confirm__cancel:disabled {
	opacity: 0.6;
	cursor: default;
}
.hud-confirm__yes:focus-visible,
.hud-confirm__cancel:focus-visible {
	outline: 2px solid var(--hud-accent);
	outline-offset: 2px;
}
.hud-tabs {
	display: flex;
	gap: 2px;
	padding: 0 10px;
	background: var(--hud-brand);
	overflow-x: auto;
	scrollbar-width: none;
}
.hud-tab {
	position: relative;
	flex: none;
	padding: 9px 10px 10px;
	border: none;
	border-radius: 8px 8px 0 0;
	background: transparent;
	color: var(--hud-soft);
	font: 600 12.5px/1 "Montserrat", sans-serif;
	cursor: pointer;
}
.hud-tab:not(.hud-tab--active):hover {
	color: #fff;
}
.hud-tab--active {
	background: #fff;
	color: var(--hud-brand-deep);
}
.hud-tab__badge {
	margin-left: 4px;
	padding: 1px 6px;
	border-radius: 8px;
	background: var(--hud-warn);
	color: var(--hud-brand-deep);
	font-size: 10.5px;
}

.hud-body {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	background: #fff;
}
.hud-empty {
	padding: 40px 24px;
	text-align: center;
	color: var(--hud-muted);
	font-size: 14px;
}
.hud-button {
	margin-top: 8px;
	padding: 7px 14px;
	border: 1px solid var(--hud-brand);
	border-radius: 8px;
	background: #fff;
	color: var(--hud-brand);
	font: 600 12.5px "Montserrat", sans-serif;
	cursor: pointer;
}

@media (max-width: 639px) {
	.hud-fab {
		right: 16px;
		bottom: 16px;
	}
	.hud-panel,
	.hud-panel--wide {
		left: 0 !important;
		right: 0 !important;
		top: auto !important;
		bottom: 0 !important;
		width: 100%;
		height: 85vh;
		border-radius: 14px 14px 0 0;
	}
	.hud-head {
		cursor: default;
	}
}

@media (prefers-reduced-motion: reduce) {
	.hud-panel,
	.hud-fab--pulse {
		animation: none;
	}
}
</style>
